/**
 * The locations (beds) context's shape, the context, and the hook that reads
 * it, apart from LocationsProvider so Fast Refresh keeps that file
 * components-only.
 *
 * @module LocationsContextDefinition
 */
import { createContext, useContext } from 'react';
import { Location, LocationMutationResponse, LocationDeleteResponse } from '../api/locations';

/**
 * Defines the shape of the Locations context data and actions.
 */
export interface LocationsContextType {
  /** The array of locations, or null if not yet loaded or an error occurred. */
  locations: Location[] | null;
  /** Indicates if the locations data is currently being loaded. */
  loading: boolean;
  /** Stores any error that occurred during data fetching. */
  error: Error | null;
  /** Function to manually trigger a refresh of the locations data. */
  refreshLocations: () => Promise<void>;
  /**
   * Function to add a new location.
   * @param data - FormData containing the new location details and optional photo.
   * @returns A promise resolving to the API response for the creation.
   */
  addLocation: (data: FormData) => Promise<LocationMutationResponse>;
  /**
   * Function to update an existing location.
   * @param locationId - The ID of the location to update.
   * @param data - FormData containing the updated location details and optional photo.
   * @returns A promise resolving to the API response for the update.
   */
  updateLocation: (locationId: number, data: FormData) => Promise<LocationMutationResponse>;
  /**
   * Function to delete a location.
   * @param locationId - The ID of the location to delete.
   * @returns A promise resolving to the API response for the deletion.
   */
  deleteLocation: (locationId: number) => Promise<LocationDeleteResponse>;
  /** Retires a location. Refused while plants are still growing in it. */
  archiveLocation: (locationId: number) => Promise<void>;
  /** Puts a retired location back. */
  restoreLocation: (locationId: number) => Promise<void>;
}

/**
 * React Context for managing location data throughout the application.
 * Provides access to location list, loading state, errors, and mutation functions.
 */
export const LocationsContext = createContext<LocationsContextType | undefined>(undefined);

/**
 * Custom hook to access the LocationsContext.
 * Ensures the hook is used within a LocationsProvider.
 * @throws {Error} If used outside of a LocationsProvider.
 * @returns The LocationsContext value.
 */
export const useLocationsContext = (): LocationsContextType => {
  const context = useContext(LocationsContext);
  if (context === undefined) {
    throw new Error('useLocationsContext must be used within a LocationsProvider');
  }
  return context;
};